import { createContext, useContext, type ReactNode } from 'react';
import { useCompanyPolicies } from '@/hooks/usePolicy';
import type { PolicyMap } from '@/lib/permissions';

// ============================================================
// PermissionsContext — one policy read, shared by every gate
// ============================================================
// The company's `company_policy_settings` rows, fetched ONCE near the root and
// handed down. Read it with usePolicyMap().
//
// ------------------------------------------------------------
// Why a context and not useCompanyPolicies() at each gate
// ------------------------------------------------------------
// useCompanyPolicies() is a useQuery, and useQuery throws "No QueryClient set"
// without a QueryClientProvider above it. Permission gates belong in leaf
// components — EmployeeSelector is embedded in a dozen dialogs — and this
// repo's component tests render those leaves directly, with only the mocks
// each one needs. Putting a query hook in a shared leaf broke 20 existing test
// files that had no reason to know about react-query.
//
// A context has the property those call sites actually need: outside a
// provider it returns {} instead of throwing, and {} resolves every capability
// to its catalog default — which is today's behaviour. So a bare-rendered
// component behaves exactly as it did before, and the real app gets the real
// policies.
//
// It also collapses N gate-driven queries into one.
//
// ------------------------------------------------------------
// The kiosk is no longer stuck on defaults
// ------------------------------------------------------------
// This used to say the PIN path kept the {} fallback "for free, which is
// correct there too", because PIN staff have no JWT and the rows were not
// readable from a kiosk. That was an accurate description of the plumbing and
// the wrong answer for the product: a company's Roles & permissions choices
// applied on the web and evaporated at the kiosk, which is what Bijan hit when
// his store lead's "Manage checklists" did nothing behind her PIN.
// useCompanyPolicies now reads the same rows for a validated pin_session via
// the limited-company-policies edge function, so the provider below carries
// real policies on both paths. {} remains the fallback for a component
// rendered outside a provider, which is still today's behaviour.

const PermissionsContext = createContext<PolicyMap | null>(null);

/** Stable empty map, so `usePolicyMap()` is safe in a dependency array. */
const EMPTY_POLICIES: PolicyMap = Object.freeze({});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { policies } = useCompanyPolicies();
  return <PermissionsContext.Provider value={policies}>{children}</PermissionsContext.Provider>;
}

/**
 * The company's stored permission rows.
 *
 * Returns an empty map outside a PermissionsProvider — deliberately, not as a
 * fallback for a bug. Every consumer passes this straight to `can()`, which
 * resolves a missing row to the catalog default, so "no provider" and "company
 * has changed nothing" are the same state and both mean current behaviour.
 */
export function usePolicyMap(): PolicyMap {
  return useContext(PermissionsContext) ?? EMPTY_POLICIES;
}
